import { z } from "zod";
import { t } from "i18next";
import ruleset from "@/_helpers/ruleset";
import { UUID } from "@/interfaces/uuid/uuid";
import { IMacros, IUnit } from "@/interfaces/nutrition/nutrition_builder";


export interface IRecipeBuilder {
    id: UUID;
    title: string;
    description?: string;
    image: string|null;
    servings: number;
    prep_time: number;
    cook_time: number;

    categories?: Array<string>;
    diet?: Array<string>;
    allergens?: Array<string>;
    cuisine?: string;

    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fibre: number;
    alcohol: number;

    ingredients: IRecipeIngredient[];
    instructions: IInstruction[];
    draft: boolean;
    published: boolean;
}

export interface IRecipeIngredient {
    id?: UUID;
    source_ingredient_id: string;
    name: string;
    amount: number;
    macros: IMacros;
    unit: IUnit;
    units: IUnit[];
}

export interface IInstruction {
    id?: UUID;
    step: number;
    instruction: string;
}

export type Library = 'custom'|'team'|'default';
export type RecipeFilter = 'all'|'published'|'unpublished';

const unitSchema = z.object({
    uuid: ruleset.uuid,
    label: ruleset.required,
    weight: z.number()
});

const macrosSchema = z.object({
    calories: z.number(),
    protein: z.number(),
    carbs: z.number(),
    fat: z.number(),
    fibre: z.number(),
    alcohol: ruleset.numberOptional,
});

export const recipeSchema = z.object({
    title: ruleset.required,
    description: ruleset.stringOptional,
    image: ruleset.required,
    draft: ruleset.booleanOptional,

    servings: ruleset.requiredNumber,
    prep_time: ruleset.numberOptional,
    cook_time: ruleset.numberOptional,
    categories: ruleset.multiselect,
    diet: ruleset.multiselectOptional,
    allergens: ruleset.multiselectOptional,
    cuisine: ruleset.selectOptional,

    calories: ruleset.numberOptional,
    protein: ruleset.numberOptional,
    carbs: ruleset.numberOptional,
    fat: ruleset.numberOptional,
    fibre: ruleset.numberOptional,
    alcohol: ruleset.numberOptional,

    instructions: z.array(z.object({
        step: ruleset.requiredNumber,
        instruction: ruleset.required,
    })),

    ingredients: z.array(z.object({
        id: ruleset.numberOptional,
        source_ingredient_id: ruleset.required,
        name: ruleset.required,
        amount: ruleset.requiredNumber,
        macros: macrosSchema,
        unit: unitSchema,
        units: z.array(unitSchema),
    })).min(1, t('components.recipeBuilder.errors.noIngredients'))
});